/**
 * Chat state management hook
 * Initializes useChat with DefaultChatTransport and manages input state
 */

import { useRef, useState } from 'react';
import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';
import type { UIMessage } from 'ai';
import { VirtuosoHandle } from 'react-virtuoso';
import { getAuthToken, getExtraRequestHeaders } from '../../lib/api/authenticated-fetch.js';
import { hasApprovedToolCallReadyToSend } from '../utils/toolApprovalHelpers.js';
import { isBusyStatus, isStreamingStatus } from '../utils/chatStatus.js';
import { useMeridianEntityContextOptional } from '../../contexts/EntityContextProvider.js';
import { logger } from '../../lib/logger.js';
import { useStreamResume } from './useStreamResume.js';

interface UseChatStateProps {
  chatId: string;
  apiBaseUrl: string;
  initialMessages: UIMessage[];
  onMessageSent?: () => void;
}

interface UseChatStateReturn {
  messages: UIMessage[];
  setMessages: (messages: UIMessage[] | ((prev: UIMessage[]) => UIMessage[])) => void;
  sendMessage: (message: {
    text: string;
    files?: any[];
    activeSkillId?: string;
  }) => Promise<void>;
  addToolApprovalResponse: (response: { id: string; approved: boolean; reason?: string }) => void;
  status: string;
  /** Abort the in-flight generation (wired to the Stop button). */
  stop: () => void;
  /** Present when the last turn failed (network / non-2xx / mid-stream). */
  error: Error | undefined;
  /** Re-run the last user turn, preserving chat state (wired to Retry). */
  regenerate: () => void;
  input: string;
  setInput: (input: string) => void;
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
  scrollContainerRef: React.RefObject<VirtuosoHandle | null>;
  /** True while streaming tokens back (narrow). */
  isStreaming: boolean;
  /** True while a request is in flight OR streaming — use this for send/disable guards. */
  isBusy: boolean;
  sendMessageRef: React.MutableRefObject<(message: {
    text: string;
    files?: any[];
    activeSkillId?: string;
  }) => Promise<void>>;
  messagesLengthRef: React.MutableRefObject<number>;
}

export function useChatState({
  chatId,
  apiBaseUrl,
  initialMessages,
  onMessageSent,
}: UseChatStateProps): UseChatStateReturn {
  // Entity-focused chats mount a MeridianEntityContextProvider; when present we
  // attach the entity context to every send so the server can scope the system
  // prompt to that entity. Replaces the host app's former global fetch hijack.
  const entityContext = useMeridianEntityContextOptional();

  // Initialize useChat with DefaultChatTransport and SDK tool approval.
  // `stop`, `error`, `regenerate`, and `setMessages` are surfaced so the UI can
  // offer a Stop button, an inline error + Retry, and reverse pagination
  // (prepending fetched older history into the live list).
  const {
    messages,
    setMessages,
    sendMessage: originalSendMessage,
    addToolApprovalResponse,
    status,
    stop,
    error,
    regenerate,
    resumeStream,
  } = useChat({
    messages: initialMessages,
    transport: new DefaultChatTransport({
      api: `${apiBaseUrl}/messages?chatId=${chatId}`,
      headers: (): Record<string, string> => {
        const token = getAuthToken();
        const extra = getExtraRequestHeaders();
        return token ? { Authorization: `Bearer ${token}`, ...extra } : { ...extra };
      },
      // Stream resume. The SDK's default reconnect URL is `${api}/${chatId}/stream`,
      // which does not work here because `api` already carries a query string —
      // it would produce `...?chatId=X/<id>/stream`. Point it at the real route.
      prepareReconnectToStreamRequest: () => ({
        api: `${apiBaseUrl}/messages/stream?chatId=${chatId}`,
      }),
      // Merged into every outgoing request body by DefaultChatTransport. Only
      // set when this is an entity-focused chat.
      ...(entityContext ? { body: { entityContext } } : {}),
      credentials: 'include',
    }),
    onFinish: () => {
      onMessageSent?.();
    },
    experimental_throttle: 50,
    // Auto-send when user approves a tool that requires approval
    sendAutomaticallyWhen: ({ messages: msgs }: { messages: any[] }) => {
      return hasApprovedToolCallReadyToSend(msgs);
    },
  });

  // Re-attach to a turn that was already running when this chat mounted (tab
  // reload, backgrounded mobile tab). No-op when the last message already has
  // its reply. Gated on `initialMessages` having been supplied, so the
  // "nothing to resume" check is never made against a list that has not loaded.
  useStreamResume({
    chatId,
    messages,
    resumeStream,
    enabled: initialMessages.length > 0,
  });

  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const scrollContainerRef = useRef<VirtuosoHandle | null>(null);
  
  // Wrap sendMessage so attachments ride along WITHOUT bypassing useChat.
  //
  // The previous implementation did a manual `fetch` and then read + DISCARDED
  // the response stream, so the assistant's reply never reached the UI. Instead
  // we send through `originalSendMessage` and pass the uploaded files in the
  // per-request `body` (`{ files }`). The server attaches them to this user
  // message (see `buildMessagesArray`): images carry `mediaType` + `url` for
  // vision, documents carry `extractedText` for context. useChat consumes the
  // streamed response normally, so it renders.
  const sendMessage = async (message: {
    text: string;
    files?: any[];
    activeSkillId?: string;
  }) => {
    const files = message.files ?? [];
    const hasFiles = files.length > 0;
    const activeSkillId =
      typeof message.activeSkillId === 'string' && message.activeSkillId.trim().length > 0
        ? message.activeSkillId.trim()
        : undefined;

    // Substitute a prompt when the user attached files but typed nothing, so the
    // model receives a non-empty instruction.
    const text =
      message.text && message.text.trim().length > 0
        ? message.text
        : hasFiles
          ? 'Please review the attached file(s).'
          : message.text;

    logger.debug('sendMessage called', {
      hasFiles,
      filesCount: files.length,
      textLength: text.length,
      activeSkillId,
      fileNames: hasFiles ? files.map((f: any) => f.filename) : undefined,
    });

    const bodyExtras: { files?: any[]; activeSkillId?: string } = {};
    if (hasFiles) bodyExtras.files = files;
    if (activeSkillId) bodyExtras.activeSkillId = activeSkillId;

    if (Object.keys(bodyExtras).length === 0) {
      await originalSendMessage({ text });
      return;
    }

    await originalSendMessage({ text }, { body: bodyExtras });
  };
  
  const sendMessageRef = useRef(sendMessage);
  sendMessageRef.current = sendMessage;
  const messagesLengthRef = useRef(messages.length);
  messagesLengthRef.current = messages.length;
  const isStreaming = isStreamingStatus(status);
  const isBusy = isBusyStatus(status);

  return {
    messages,
    setMessages,
    sendMessage,
    addToolApprovalResponse,
    status,
    stop,
    error,
    regenerate,
    input,
    setInput,
    inputRef,
    scrollContainerRef,
    isStreaming,
    isBusy,
    sendMessageRef,
    messagesLengthRef,
  };
}
